// src/prototype/pages/EAInitiativeCommandCenterPage.tsx
// ============================================================
// EA INITIATIVE COMMAND CENTER
// Route: /ea/execute/:initiativeId
// Storefront chrome; data from this branch’s command-center API.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Award,
  BarChart3,
  Calendar,
  Check,
  Clock,
  FileText,
  List,
  RotateCcw,
  Scale,
  Shield,
  Target,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import { EAJourneyBar } from '../components/EAJourneyBar';
import { StatTile } from '../components/StatTile';
import { SectionCard } from '../components/SectionCard';
import { Collapse } from '../components/Collapse';
import { ExecStatusPill } from '../components/ExecStatusPill';
import { ApprovalChain } from '../components/ApprovalChain';
import { CFOPanel } from '../components/CFOPanel';
import { MilestoneRow } from '../components/MilestoneRow';
import { WorkstreamsTable, type WorkstreamRow } from '../components/WorkstreamsTable';
import { ActivationChecklist } from '../components/ActivationChecklist';
import { TeamList, type TeamMemberRow } from '../components/TeamList';
import {
  fetchCommandCenter,
  peekCommandCenter,
  CommandCenterError,
  resolveApiPath,
  createWorkstream,
  updateWorkstream,
  deleteWorkstream,
  updateActivationChecklistItem,
  completeApprovalSignoff,
  createTeamMember,
  deleteTeamMember,
} from '../services/commandCenterService';
import {
  mapCommandCenter,
  resourceContentPreview,
  type CommandCenterViewModel,
  type MappedResourceItem,
} from '../utils/mapCommandCenter';
import {
  approvalStepsFromProgress,
  cfoSummaryFromViewModel,
} from '../utils/executeChrome';
import { SEVERITY_DOT } from '../theme/decisionTokens';
import { useEA } from '../contexts/EAContext';
import { EA_CLIENT_ID } from '../constants/eaClient';
import type { ChecklistItem } from '../components/ActivationChecklist';
import { ArtifactPreviewModal } from '../components/artifacts/ArtifactPreviewModal';
import { MarkdownProse } from '../components/MarkdownProse';
import { fetchApprovalArtifact } from '../services/approvalArtifactsService';
import { mapApprovalArtifactToDva } from '../utils/mapApprovalArtifact';
import { formatPlainExecutiveText, resolveApprovalArtifactId } from '../utils/executiveCopy';
import type { ArtifactType, DvaArtifact } from '../artifacts/types/artifactTypes';
import { ExecutionPlaybooksError } from '../services/executionPlaybooksService';

type LoadStatus = 'idle' | 'loading' | 'success' | 'error';

function ResourcePreviewModal({
  title,
  content,
  onClose,
}: {
  title: string;
  content: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e5e7eb]">
          <h3 className="text-sm font-semibold text-[#18365E]">{title}</h3>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-[#F5F7FA] rounded-lg">
            <X className="w-4 h-4 text-[#717182]" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">
          <MarkdownProse>{content}</MarkdownProse>
        </div>
      </div>
    </div>
  );
}

function MetricTable({
  title,
  subtitle,
  rows,
}: {
  title: string;
  subtitle?: string;
  rows: { label: string; baseline: string; current: string; target: string }[];
}) {
  if (!rows.length) return null;
  return (
    <div className="mb-4 last:mb-0">
      <p className={`text-[10px] font-semibold text-[#717182] uppercase tracking-wide ${subtitle ? 'mb-0.5' : 'mb-2'}`}>
        {title}
      </p>
      {subtitle && <p className="text-[10px] text-[#94a3b8] mb-2 leading-relaxed">{subtitle}</p>}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">
              <th className="text-left py-1.5">Metric</th>
              <th className="text-left py-1.5">Baseline</th>
              <th className="text-left py-1.5">Current</th>
              <th className="text-left py-1.5">Target</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-t border-[#f1f5f9]">
                <td className="py-2 font-medium text-[#18365E]">{row.label}</td>
                <td className="py-2 text-[#717182]">{row.baseline}</td>
                <td className="py-2 text-[#717182]">{row.current}</td>
                <td className="py-2 font-semibold text-emerald-700">{row.target}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function EAInitiativeCommandCenterPage() {
  const { initiativeId } = useParams<{ initiativeId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { canAccessProSteps } = useEA();
  const clientId = EA_CLIENT_ID;
  const useCaseId = initiativeId ?? '';

  const cachedCenter = useCaseId ? peekCommandCenter(clientId, useCaseId) : null;
  const [loadStatus, setLoadStatus] = useState<LoadStatus>(() => (cachedCenter ? 'success' : 'idle'));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [viewModel, setViewModel] = useState<CommandCenterViewModel | null>(() =>
    cachedCenter ? mapCommandCenter(cachedCenter) : null,
  );
  const [previewResource, setPreviewResource] = useState<{ title: string; content: string } | null>(null);
  const [previewArtifact, setPreviewArtifact] = useState<DvaArtifact | null>(null);
  const [artifactLoading, setArtifactLoading] = useState(false);
  const [artifactError, setArtifactError] = useState<string | null>(null);
  const [pendingArtifactType, setPendingArtifactType] = useState<ArtifactType | null>(null);
  const [artifactRetryKey, setArtifactRetryKey] = useState<ArtifactType | null>(null);
  const [editableWorkstreams, setEditableWorkstreams] = useState<WorkstreamRow[]>([]);
  const [approvalCompleted, setApprovalCompleted] = useState(0);
  const [editableTeam, setEditableTeam] = useState<TeamMemberRow[]>([]);
  const [approvalBusy, setApprovalBusy] = useState(false);

  useEffect(() => {
    if (!canAccessProSteps) navigate('/ea/upgrade', { replace: true });
  }, [canAccessProSteps, navigate]);

  const applyApprovalSignoff = useCallback((signoff: {
    completed_count?: number;
    completedCount?: number;
    steps?: { id: string; label: string; cfo?: boolean; status: string }[];
  } | null | undefined) => {
    if (!signoff) return;
    const count = signoff.completed_count ?? signoff.completedCount ?? 0;
    setApprovalCompleted(Math.max(0, Math.min(5, count)));
  }, []);

  const handleApprovalAdvance = useCallback(async () => {
    if (!useCaseId || approvalBusy) return;
    // Stage 1 is automatic (sponsor assignment) — only stages 2–5 can be completed manually.
    if (approvalCompleted < 1) return;
    setApprovalBusy(true);
    try {
      const result = await completeApprovalSignoff(clientId, useCaseId);
      if (result) {
        applyApprovalSignoff(result);
      } else {
        setApprovalCompleted((prev) => Math.min(5, prev + 1));
      }
    } catch (err) {
      console.error('[CommandCenter] approval complete failed:', err);
    } finally {
      setApprovalBusy(false);
    }
  }, [clientId, useCaseId, approvalBusy, approvalCompleted, applyApprovalSignoff]);

  const handleTeamAdd = useCallback(async (member: TeamMemberRow) => {
    if (!useCaseId) return;
    const optimisticId = member.id ?? `local-${Date.now()}`;
    setEditableTeam((prev) => [...prev, { ...member, id: optimisticId }]);
    try {
      const created = await createTeamMember(clientId, useCaseId, {
        name: member.name || '',
        role: member.role,
        title: member.title || undefined,
      });
      setEditableTeam((prev) =>
        prev.map((row) =>
          row.id === optimisticId
            ? {
                id: created.team_member_id ?? optimisticId,
                name: created.name ?? member.name,
                role: created.role ?? member.role,
                title: created.title ?? member.title,
              }
            : row,
        ),
      );
      if (created.approval_signoff) {
        applyApprovalSignoff(created.approval_signoff);
      }
    } catch (err) {
      setEditableTeam((prev) => prev.filter((row) => row.id !== optimisticId));
      console.error('[CommandCenter] add team member failed:', err);
      throw err;
    }
  }, [clientId, useCaseId, applyApprovalSignoff]);

  const handleTeamRemove = useCallback(async (member: TeamMemberRow, index: number) => {
    if (!useCaseId) return;
    const previous = editableTeam;
    setEditableTeam((prev) => prev.filter((_, i) => i !== index));
    if (!member.id || member.id.startsWith('local-') || member.id.startsWith('api-')) {
      return;
    }
    try {
      await deleteTeamMember(clientId, useCaseId, member.id);
    } catch (err) {
      setEditableTeam(previous);
      console.error('[CommandCenter] remove team member failed:', err);
      throw err;
    }
  }, [clientId, useCaseId, editableTeam]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [initiativeId]);

  const loadData = useCallback(async (signal?: { cancelled: boolean }) => {
    if (!useCaseId) {
      setLoadStatus('error');
      setErrorMessage('No initiative ID provided.');
      return;
    }
    const cached = peekCommandCenter(clientId, useCaseId);
    if (cached) {
      setViewModel(mapCommandCenter(cached));
      setLoadStatus('success');
      setErrorMessage(null);
      return;
    }

    setLoadStatus('loading');
    setErrorMessage(null);
    try {
      const response = await fetchCommandCenter(clientId, useCaseId);
      if (signal?.cancelled) return;
      setViewModel(mapCommandCenter(response));
      setLoadStatus('success');
    } catch (err) {
      if (signal?.cancelled) return;
      setErrorMessage(err instanceof CommandCenterError ? err.message : 'Failed to load command center.');
      setViewModel(null);
      setLoadStatus('error');
    }
  }, [clientId, useCaseId]);

  useEffect(() => {
    const signal = { cancelled: false };
    loadData(signal);
    return () => { signal.cancelled = true; };
  }, [loadData]);

  useEffect(() => {
    if (!viewModel) {
      setEditableWorkstreams([]);
      setEditableTeam([]);
      return;
    }
    setEditableWorkstreams(
      viewModel.workstreams.map((workstream) => ({
        id: workstream.id,
        name: workstream.name,
        owner: workstream.owner,
        status: workstream.status,
        target: workstream.targetDate,
        notes: workstream.notes,
      })),
    );
    setEditableTeam(
      viewModel.team.map((member, index) => ({
        id: member.id ?? `api-${index}-${member.role}`,
        role: member.role,
        name: member.name,
        title: member.title,
      })),
    );
    setApprovalCompleted(viewModel.approvalSignoff?.completedCount ?? 0);
  }, [viewModel]);

  const reloadCommandCenter = useCallback(async () => {
    const response = await fetchCommandCenter(clientId, useCaseId);
    setViewModel(mapCommandCenter(response));
  }, [clientId, useCaseId]);

  const handleWorkstreamSave = useCallback(async (row: WorkstreamRow) => {
    const payload = {
      name: row.name,
      owner: row.owner,
      status: row.status,
      target: row.target,
      notes: row.notes,
    };

    if (row.id) {
      await updateWorkstream(clientId, useCaseId, row.id, payload);
    } else {
      await createWorkstream(clientId, useCaseId, payload);
    }
    // The row ids and derived targets come from the server, so re-read rather
    // than patching local state.
    await reloadCommandCenter();
  }, [clientId, useCaseId, reloadCommandCenter]);

  const handleChecklistToggle = useCallback(async (item: ChecklistItem, nextDone: boolean) => {
    if (!item.index) {
      throw new CommandCenterError('Checklist item is missing an index.');
    }

    // Optimistic UI so the summary updates immediately.
    setViewModel((prev) => {
      if (!prev) return prev;
      const nextItems = prev.activationChecklist.map((row) =>
        row.index === item.index
          ? { ...row, done: nextDone, status: nextDone ? 'Done' : 'Pending' }
          : row,
      );
      const completed = nextItems.filter((row) => row.done).length;
      return {
        ...prev,
        activationChecklist: nextItems,
        checklistSummary: {
          total: nextItems.length,
          completed,
          progressPercent: nextItems.length
            ? Math.round((completed / nextItems.length) * 100)
            : 0,
        },
      };
    });

    try {
      const result = await updateActivationChecklistItem(clientId, useCaseId, {
        index: item.index,
        done: nextDone,
      });
      if (result.activation_checklist.length > 0) {
        setViewModel((prev) => {
          if (!prev) return prev;
          const nextItems = result.activation_checklist.map((row) => ({
            index: row.index,
            label: row.label,
            done: row.done,
            status: row.status,
          }));
          const summary = result.activation_checklist_summary;
          return {
            ...prev,
            activationChecklist: nextItems,
            checklistSummary: {
              total: summary?.total ?? nextItems.length,
              completed: summary?.completed ?? nextItems.filter((row) => row.done).length,
              progressPercent:
                summary?.progress_percent
                ?? (nextItems.length
                  ? Math.round((nextItems.filter((row) => row.done).length / nextItems.length) * 100)
                  : 0),
            },
          };
        });
      } else {
        await reloadCommandCenter();
      }
    } catch (err) {
      await reloadCommandCenter();
      throw err;
    }
  }, [clientId, useCaseId, reloadCommandCenter]);

  const handleWorkstreamDelete = useCallback(async (row: WorkstreamRow) => {
    if (!row.id) return;
    // DMD-1210: mirror API rule — never wipe the last workstream (avoids default re-seed).
    if (editableWorkstreams.length <= 1) {
      throw new CommandCenterError(
        'At least one workstream is required. You cannot delete the last remaining workstream.',
      );
    }
    await deleteWorkstream(clientId, useCaseId, row.id);
    await reloadCommandCenter();
  }, [clientId, useCaseId, editableWorkstreams.length, reloadCommandCenter]);

  const handleBack = () => navigate('/ea/activate');
  const openBrief = () =>
    navigate(`/ea/use-case/${useCaseId}/brief`, {
      state: { from: 'activate', returnTo: location.pathname },
    });
  const openRoiModel = () =>
    navigate(`/ea/roi-model/${useCaseId}`, { state: { from: 'execute' } });
  const openRationale = () =>
    navigate(`/ea/use-case/${useCaseId}/rationale`, { state: { from: 'execute' } });

  const closeArtifactModal = useCallback(() => {
    setPreviewArtifact(null);
    setArtifactLoading(false);
    setArtifactError(null);
    setPendingArtifactType(null);
    setArtifactRetryKey(null);
  }, []);

  const loadApprovalArtifact = useCallback(async (type: ArtifactType) => {
    setArtifactRetryKey(type);
    setPendingArtifactType(type);
    setPreviewArtifact(null);
    setPreviewResource(null);
    setArtifactError(null);
    setArtifactLoading(true);
    try {
      const response = await fetchApprovalArtifact(clientId, useCaseId, type);
      setPreviewArtifact(mapApprovalArtifactToDva(response.artifact));
      setArtifactError(null);
    } catch (err) {
      const message = err instanceof ExecutionPlaybooksError
        ? err.message
        : 'Failed to load approval artifact.';
      setPreviewArtifact(null);
      setArtifactError(message);
      console.error('[CommandCenter] approval-artifact failed:', type, err);
    } finally {
      setArtifactLoading(false);
    }
  }, [clientId, useCaseId]);

  const handleResourceAction = (item: MappedResourceItem) => {
    const approvalArtifactId = resolveApprovalArtifactId(item.id);
    if (approvalArtifactId) {
      void loadApprovalArtifact(approvalArtifactId as ArtifactType);
      return;
    }
    if (item.action === 'download' && item.path) {
      window.open(resolveApiPath(item.path), '_blank');
      return;
    }
    if (item.action === 'navigate') {
      if (item.id.includes('roi')) openRoiModel();
      else if (item.id.includes('scoring')) openRationale();
      else openBrief();
      return;
    }
    if (item.action === 'render' && item.content) {
      setPreviewResource({
        title: formatPlainExecutiveText(item.label),
        content: resourceContentPreview(item.content),
      });
      return;
    }
    if (item.path) window.open(resolveApiPath(item.path), '_blank');
  };

  const shell = (body: React.ReactNode) => (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="px-6 pt-3 pb-2 print:hidden">
        <EAJourneyBar />
      </div>
      {body}
    </div>
  );

  if (loadStatus === 'loading' || loadStatus === 'idle') {
    return shell(
      <div className="flex-1 flex items-center justify-center py-24">
        <div className="text-center">
          <div className="w-10 h-10 rounded-full border-2 border-[#18365E]/30 border-t-[#18365E] animate-spin mx-auto mb-4" />
          <p className="text-sm font-semibold text-[#18365E]">Loading Command Center…</p>
        </div>
      </div>,
    );
  }

  if (loadStatus === 'error' || !viewModel) {
    return shell(
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="bg-white rounded-xl border border-[#e5e7eb] px-6 py-14 text-center">
          <p className="text-sm font-bold text-[#18365E]">
            {useCaseId ? 'Unable to load Command Center' : 'Initiative not found'}
          </p>
          <p className="text-xs text-[#717182] mt-1 max-w-md mx-auto">
            {errorMessage || `No initiative matches ${initiativeId}.`}
          </p>
          <div className="mt-5 flex gap-3 justify-center">
            <button
              type="button"
              onClick={handleBack}
              className="bg-[#FF6E4C] text-white text-xs font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5 hover:bg-[#e85f3f] transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Execution portfolio
            </button>
            <button
              type="button"
              onClick={() => loadData()}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#18365E] text-white rounded-lg text-xs font-semibold"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Retry
            </button>
          </div>
        </div>
      </div>,
    );
  }

  const { header, overview, milestones, risks, metrics, resources } = viewModel;
  const approval = approvalStepsFromProgress(approvalCompleted);
  const cfo = cfoSummaryFromViewModel(viewModel);
  const openRisks = risks.filter((risk) => risk.status === 'Open').length;
  const milestonesDone = viewModel.milestoneSummary.complete;
  const cfoArtifacts = resources.approvalMaterials
    .filter((item) => item.available)
    .slice(0, 3)
    .map((item) => ({
      id: item.id,
      label: item.label,
      onClick: () => handleResourceAction(item),
    }));

  const meta: [string, string][] = [
    ['Executive sponsor', header.sponsor],
    ['Initiative owner', header.owner],
    ['Current phase', header.phase],
    ['Timeline', header.timeline],
  ];

  const extraResources = [
    ...resources.decisionReferences,
    ...resources.executionMaterials,
  ];

  return shell(
    <>
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-[#e5e7eb] px-6 py-2.5 print:hidden">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#717182] hover:text-[#18365E] transition-colors shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Execution portfolio
          </button>
          <span className="text-[#e5e7eb]" aria-hidden="true">|</span>
          <span className="text-xs font-bold text-[#18365E] truncate">{header.title}</span>
          <button
            type="button"
            onClick={openBrief}
            className="ml-auto text-xs font-semibold text-[#18365E] hover:text-[#FF6E4C] flex items-center gap-1 shrink-0 transition-colors"
          >
            <FileText className="w-3.5 h-3.5" />
            View evaluation brief
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-4">
        <div className="bg-white rounded-xl border border-[#e5e7eb] p-6">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="text-[10px] font-semibold text-[#FF6E4C] uppercase tracking-wide">{header.categoryName}</span>
                <ExecStatusPill status={header.status} />
              </div>
              <h1 className="text-2xl font-bold text-[#18365E] leading-tight">{header.title}</h1>
              {overview.objective && (
                <p className="text-sm text-[#4b5563] leading-relaxed mt-2 max-w-3xl">{overview.objective}</p>
              )}
            </div>
            <button
              type="button"
              onClick={openRoiModel}
              className="shrink-0 bg-[#18365E] text-white text-xs font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 hover:bg-[#122a4a] transition-colors"
            >
              <TrendingUp className="w-3.5 h-3.5" />
              ROI Modeling
            </button>
          </div>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 border-t border-[#f1f5f9] pt-4">
            {meta.map(([label, value]) => (
              <div key={label}>
                <p className="text-[10px] text-[#717182] font-semibold uppercase tracking-wide">{label}</p>
                <p className="text-xs font-bold text-[#18365E] mt-0.5">{value}</p>
              </div>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatTile variant="dominant" label="Annual ROI" value={header.roiRange} icon={<TrendingUp className="w-5 h-5" />} />
            <StatTile label="Payback" value={header.payback} icon={<Clock className="w-4 h-4" />} />
            <StatTile label="ROI Confidence" value={header.confidence} icon={<Shield className="w-4 h-4" />} />
            <StatTile
              label="Milestones"
              value={`${milestonesDone} of ${viewModel.milestoneSummary.total || milestones.length} complete`}
              icon={<Calendar className="w-4 h-4" />}
            />
          </div>

          {overview.nextAction && (
            <div className="mt-4 flex items-start gap-2 border-t border-[#f1f5f9] pt-3">
              <Target className="w-3.5 h-3.5 text-[#FF6E4C] shrink-0 mt-0.5" />
              <p className="text-[11px] text-[#717182] leading-relaxed">
                <span className="font-semibold text-[#18365E]">Recommended next action</span>
                <span className="mx-1.5 text-[#e5e7eb]" aria-hidden="true">|</span>
                {overview.nextAction}
              </p>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-[1fr_340px] gap-4 items-start">
          <div className="space-y-4">
            <SectionCard icon={<Scale className="w-3.5 h-3.5" />} title="Approval & Sign-off" accent>
              <div className="grid gap-5 lg:grid-cols-[240px_minmax(0,1fr)]">
                <ApprovalChain
                  steps={approval}
                  onCompleteCurrent={handleApprovalAdvance}
                  completing={approvalBusy}
                />
                <CFOPanel cfo={cfo} onOpenRoiModel={openRoiModel} artifacts={cfoArtifacts} />
              </div>
            </SectionCard>

            <SectionCard icon={<Calendar className="w-3.5 h-3.5" />} title="Milestones">
              {milestones.length === 0 ? (
                <p className="text-xs text-[#717182]">No milestones defined yet.</p>
              ) : (
                <div className="space-y-2.5">
                  {milestones.map((milestone) => (
                    <MilestoneRow
                      key={milestone.id}
                      milestone={{
                        title: milestone.title,
                        phase: milestone.phase,
                        owner: milestone.owner,
                        due: milestone.dueDate,
                        status: milestone.status,
                        pct: milestone.completionPct,
                        blocker: milestone.blockers,
                      }}
                    />
                  ))}
                </div>
              )}
            </SectionCard>

            <Collapse
              icon={<List className="w-3.5 h-3.5" />}
              title="Workstreams"
              meta={editableWorkstreams.length > 0 ? `${editableWorkstreams.length} active` : 'None yet'}
              defaultOpen
            >
              <WorkstreamsTable
                workstreams={editableWorkstreams}
                onSave={handleWorkstreamSave}
                onDelete={handleWorkstreamDelete}
              />
            </Collapse>

            <Collapse icon={<Shield className="w-3.5 h-3.5" />} title="Risk Register" meta={`${openRisks} open`}>
              {risks.length === 0 ? (
                <p className="text-xs text-[#717182]">No risks recorded yet.</p>
              ) : (
                <div className="space-y-3">
                  {risks.map((risk) => (
                    <div key={risk.id} className="bg-[#F5F7FA] rounded-lg border border-[#e5e7eb] p-3.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${SEVERITY_DOT[risk.severity] ?? 'bg-slate-400'}`} />
                        <span className="text-xs font-bold text-[#18365E]">{risk.title}</span>
                        <span className="text-[10px] font-semibold text-[#717182]">
                          {risk.severity} · {risk.likelihood} likelihood
                        </span>
                        <span
                          className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            risk.status === 'Open'
                              ? 'bg-white border border-[#e5e7eb] text-[#717182]'
                              : 'bg-emerald-50 text-emerald-700'
                          }`}
                        >
                          {risk.status}
                        </span>
                      </div>
                      {risk.description && (
                        <p className="text-xs text-[#4b5563] leading-relaxed mt-1.5">{risk.description}</p>
                      )}
                      {risk.mitigation && (
                        <p className="text-xs text-[#4b5563] leading-relaxed mt-1">
                          <b className="text-[#18365E]">Mitigation:</b> {risk.mitigation}{' '}
                          <span className="text-[#717182]">· {risk.owner}</span>
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Collapse>

            {(metrics.financial.length > 0 || metrics.operational.length > 0 || metrics.delivery.length > 0 || metrics.banner) && (
              <Collapse icon={<BarChart3 className="w-3.5 h-3.5" />} title="Metrics">
                {(metrics.banner || metrics.roiNarrative) && (
                  <div className="mb-3 rounded-lg border border-[#e5e7eb] bg-[#F5F7FA] px-3 py-2.5">
                    <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-1">
                      ROI model estimate
                    </p>
                    <p className="text-xs text-[#4b5563] leading-relaxed">
                      {metrics.banner ?? metrics.roiNarrative}
                    </p>
                  </div>
                )}
                <MetricTable
                  title="Financial"
                  subtitle="Business targets — may differ from ROI model estimates above"
                  rows={metrics.financial}
                />
                <MetricTable title="Operational" rows={[...metrics.operational, ...metrics.clinical]} />
                <MetricTable title="Delivery" rows={metrics.delivery} />
              </Collapse>
            )}

            {extraResources.length > 0 && (
              <Collapse icon={<FileText className="w-3.5 h-3.5" />} title="Resources" meta={`${extraResources.length}`}>
                <div className="grid sm:grid-cols-2 gap-2">
                  {extraResources.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleResourceAction(item)}
                      disabled={!item.available}
                      className="flex items-start gap-2 p-3 border border-[#e5e7eb] rounded-lg text-left hover:border-[#18365E] disabled:opacity-50"
                    >
                      <FileText className="w-3.5 h-3.5 text-[#18365E] mt-0.5 shrink-0" />
                      <span>
                        <span className="block text-xs font-semibold text-[#18365E]">{item.label}</span>
                        {item.desc && <span className="block text-[10px] text-[#717182] mt-0.5">{item.desc}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              </Collapse>
            )}
          </div>

          <div className="space-y-4">
            <SectionCard icon={<Check className="w-3.5 h-3.5" />} title="Activation Checklist" accent>
              {viewModel.activationChecklist.length === 0 ? (
                <p className="text-xs text-[#717182]">No activation items yet.</p>
              ) : (
                <ActivationChecklist
                  items={viewModel.activationChecklist}
                  onToggle={handleChecklistToggle}
                />
              )}
            </SectionCard>

            <SectionCard icon={<Users className="w-3.5 h-3.5" />} title="Team">
              <TeamList
                team={editableTeam}
                onAdd={handleTeamAdd}
                onRemove={handleTeamRemove}
              />
            </SectionCard>

            {overview.whyApproved && (
              <SectionCard icon={<Award className="w-3.5 h-3.5" />} title="Why This Was Approved">
                <p className="text-xs text-[#4b5563] leading-relaxed">{overview.whyApproved}</p>
                <button
                  type="button"
                  onClick={openBrief}
                  className="mt-3 text-xs font-semibold text-[#18365E] hover:text-[#FF6E4C] flex items-center gap-1 transition-colors"
                >
                  Full evaluation brief
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </SectionCard>
            )}

            {overview.expectedOutcomes.length > 0 && (
              <SectionCard icon={<Target className="w-3.5 h-3.5" />} title="Expected Outcomes">
                <ul className="space-y-2">
                  {overview.expectedOutcomes.map((outcome) => (
                    <li key={outcome} className="flex items-start gap-2 text-xs text-[#4b5563] leading-relaxed">
                      <ArrowRight className="w-3.5 h-3.5 text-[#FF6E4C] mt-0.5 shrink-0" />
                      {outcome}
                    </li>
                  ))}
                </ul>
              </SectionCard>
            )}
          </div>
        </div>
      </div>

      {previewResource && (
        <ResourcePreviewModal
          title={previewResource.title}
          content={previewResource.content}
          onClose={() => setPreviewResource(null)}
        />
      )}

      {(previewArtifact || artifactLoading || artifactError) && (
        <ArtifactPreviewModal
          artifact={previewArtifact}
          loading={artifactLoading}
          error={artifactError}
          pendingType={pendingArtifactType ?? undefined}
          onRetry={artifactRetryKey ? () => void loadApprovalArtifact(artifactRetryKey) : undefined}
          onClose={closeArtifactModal}
        />
      )}
    </>,
  );
}
